import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@material-ui/core';

import css from './speakerDetailed.module.scss';
import { Speaker } from '../../../services/models';
import { Image } from '../../../components/layout/pageLayout/PageLayout';
import Dialog from '../../../components/dialog/Dialog';
import { LeftArrowIconButton, RightArrowButton, RightArrowIconButton } from '../../../components/buttons/Buttons';
import { Mobile, useIsTablet, usePageMargin } from '../../../components/layout/responsive';
import { LocationRow, TagChip } from '../../../components/elements/Elements';
import { Swiper, SwiperSlide } from '../../../components/elements/Swiper';

export default function SpeakerDetailedModal({ open, onClose, speaker, onPrev, onNext }: {
  open: boolean, onClose?: () => void, speaker?: Speaker, onPrev?: () => void, onNext?: () => void,
}) {
  const { t } = useTranslation();
  const isTablet = useIsTablet();
  const pageMargin = usePageMargin();
  const [item, setItem] = useState(speaker);
  const hasNavigations = onPrev || onNext;
  const hasTags = item?.tags && item?.tags.length > 0;
  useEffect(() => {
    if (speaker) setItem(speaker);
  }, [speaker]);
  let children = null;
  if (item) {
    const locationBlock = (item.location || item.organization) && (
      <LocationRow
        className={css.SpeakerDetailedModal__location}
        title={<Typography variant="subtitle1">{item.location}</Typography>}
        subtitle={item.organization}
      />
    );
    const description = item.description && (
      <Typography className={css.SpeakerDetailedModal__info} dangerouslySetInnerHTML={{ __html: item.description }} />
    );
    const linkBlock = item.link && (
      <RightArrowButton className={css.SpeakerDetailedModal__link} href={item.link}>
        {t('speakers_page.speaker_link')}
      </RightArrowButton>
    );
    const tagsBlock = hasTags && (
      <Box className={css.SpeakerDetailedModal__tagList}>
        {item.tags!.map(i => <TagChip key={i.name} className={css.tag} tag={i} />)}
      </Box>
    );
    children = (
      <Box display="flex" flexDirection="column" alignItems="stretch">
        <Mobile invert>
          <Box display="flex" justifyContent="space-between" alignItems="center" className={css.title}>
            <Typography variant="h2">{item.full_name}</Typography>
            {hasNavigations && (
              <Box className={css.navigation}>
                <LeftArrowIconButton aria-label="предыдущий" className={css.navButton} onClick={onPrev}
                                     disabled={!onPrev} />
                <RightArrowIconButton aria-label="следующий" className={css.navButton} onClick={onNext}
                                      disabled={!onNext} />
              </Box>
            )}
          </Box>
          <Box className={css.container}>
            <Image className={css.SpeakerDetailedModal__photo} src={item.image} alt={item.full_name} />
            {locationBlock}
            {isTablet && tagsBlock}
            {description}
            {linkBlock}
          </Box>
          {!isTablet && tagsBlock}
        </Mobile>
        <Mobile>
          <Box
            display="flex" alignItems="center" justifyContent={hasNavigations ? 'space-between' : 'center'}
            className={css.navigation}
          >
            {hasNavigations && <LeftArrowIconButton aria-label="предыдущий" className={css.navButton} onClick={onPrev}
                                                    disabled={!onPrev} />}
            <Image className={css.SpeakerDetailedModal__photo} src={item.image} alt={item.full_name} />
            {hasNavigations && <RightArrowIconButton aria-label="следующий" className={css.navButton} onClick={onNext}
                                                     disabled={!onNext} />}
          </Box>
          <Typography className={css.SpeakerDetailedModal__name} variant="h2">{item.full_name}</Typography>
          {locationBlock}
          {description}
          {linkBlock}
          {hasTags && (
            <Swiper
              className={css.SpeakerDetailedModal__tagList}
              freeMode
              watchSlidesProgress={true} watchSlidesVisibility={true} slidesPerView="auto"
              slidesOffsetBefore={pageMargin}
              slidesOffsetAfter={pageMargin}
              spaceBetween={12}
            >
              {item.tags!.map(i => (
                <SwiperSlide key={i.name} className={css.SwiperSlide}>
                  <TagChip className={css.tag} tag={i} />
                </SwiperSlide>
              ))}
            </Swiper>
          )}
        </Mobile>
      </Box>
    );
  }
  return (
    <Dialog className={classnames(css.SpeakerDetailedModal)} fullWidth open={open} onClose={onClose}>
      {children}
    </Dialog>
  );
}

